import * as React from 'react';
import { ReactComponent as ExpandMoreIcon } from 'assets/svg/expand_more.svg';
import { ReactComponent as ChevronRightIcon } from 'assets/svg/chevron_right.svg';
import {
  DropdownContainer,
  DropdownContent,
  DropdownTitle,
  StyledSummary,
  StyledSummaryContainer,
  TitleContainer,
} from './Dropdown.styles';
import type { DropdownProps, DropdownWithHeaderProps } from './Dropdown.types';
import useDropdown from 'utils/customHooks/useDropdown';

export const Dropdown = React.forwardRef<HTMLDivElement, DropdownProps>(
  ({ children, isOpen = true, title, className, onChange = () => {} }, ref) => {
    const { isDropdownOpen, handleChange } = useDropdown(isOpen, onChange);
    return (
      <DropdownContainer className={className} ref={ref}>
        <DropdownContent
          onClick={handleChange}
          role='button'
          tabIndex={0}
          onKeyPress={handleChange}
          aria-expanded={isDropdownOpen}
        >
          <DropdownTitle>{title}</DropdownTitle>
          {isDropdownOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </DropdownContent>
        {isDropdownOpen ? children : null}
      </DropdownContainer>
    );
  },
);

Dropdown.displayName = 'Dropdown';

export const DropdownWithHeader = React.forwardRef<HTMLDivElement, DropdownWithHeaderProps>(
  ({ children, isOpen = true, header, className, onChange = () => {} }, ref) => {
    const { isDropdownOpen, handleChange } = useDropdown(isOpen, onChange);
    let headerContent = null;
    if (typeof header === 'function') {
      headerContent = header({ isDropdownOpen, handleChange });
    }

    return (
      <DropdownContainer className={className} ref={ref}>
        {headerContent}
        {isDropdownOpen ? children : null}
      </DropdownContainer>
    );
  },
);

DropdownWithHeader.displayName = 'DropdownWithHeader';

export const DropdownWithSummary = React.forwardRef<HTMLDivElement, DropdownProps>(
  ({ children, isOpen = true, title, summary = null, className, onChange = () => {} }, ref) => {
    const { isDropdownOpen, handleChange } = useDropdown(isOpen, onChange);

    return (
      <DropdownContainer className={className} ref={ref}>
        <DropdownContent
          onClick={handleChange}
          role='button'
          tabIndex={0}
          onKeyPress={handleChange}
          aria-expanded={isDropdownOpen}
        >
          <StyledSummaryContainer>
            <TitleContainer>
              <DropdownTitle>{title}</DropdownTitle>
              {isDropdownOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </TitleContainer>
            <StyledSummary>{summary}</StyledSummary>
          </StyledSummaryContainer>
        </DropdownContent>
        {isDropdownOpen ? children : null}
      </DropdownContainer>
    );
  },
);

DropdownWithSummary.displayName = 'DropdownWithSummary';
